import { ItemDetail } from "./projectRegister.types";

import { ZERO } from "@/app/constants/constants";
import { Any } from "@/interfaces/globals";

interface HasReferencePrefix {
  customer?: { shortName?: string } | null;
  prospect?: { shortName?: string } | null;
  workspace?: { workspaceCode?: string } | null;
  referenceNumber?: string | null;
}

export const buildReferencePrefix = (
  project: HasReferencePrefix | null | undefined,
): string | undefined => {
  if (!project) {
    return undefined;
  }

  // For existing reference numbers, extract the original prefix by splitting
  // at the last space. The suffix is always numeric, so the last space is the
  // reliable boundary even if the shortName itself contains spaces.
  // handles cases where prefix might change for same client like shortName or workspaceCode change
  if (project.referenceNumber) {
    const lastSpaceIndex = project.referenceNumber.lastIndexOf(" ");
    if (lastSpaceIndex > ZERO) {
      return project.referenceNumber.slice(ZERO, lastSpaceIndex);
    }
    // Reference exists but has no space (e.g. legacy "123" or "LEGACY-123").
    return undefined;
  }

  const shortName =
    project?.customer?.shortName || project?.prospect?.shortName;

  if (!shortName) {
    return undefined;
  }

  const wsCode = project?.workspace?.workspaceCode;

  return wsCode ? `${shortName}${wsCode}` : shortName;
};

// Recursive helper to calculate item price (including nested subItems)

/**
 * Calculates base item price.
 * If an item has subItems, its quantity multiplies
 * the aggregated price of all descendants.
 * @param {ItemDetail} item - The item to calculate price for
 * @param {number} depth - The current nesting depth
 * @returns {number} The calculated base item price
 */
export const calculateItemPriceRecursive = (
  item: ItemDetail,
  depth: number = 0,
): number => {
  if (depth > 5) {
    return 0;
  }

  const quantity = item.quantity ?? 0;
  const subItems = item.subItems;

  if (subItems?.length) {
    let subTotal = 0;

    for (let i = 0; i < subItems.length; i++) {
      subTotal += calculateItemPriceRecursive(subItems[i], depth + 1);
    }

    return quantity * subTotal;
  }

  return quantity * (item.price ?? 0);
};

// Helper function to validate nesting depth (max 5 levels)
export const validateNestingDepth = (
  item: Any,
  depth: number = 0,
  maxDepth: number = 5,
): boolean => {
  if (depth > maxDepth) {
    return false;
  }

  if (item?.subItems && Array.isArray(item.subItems)) {
    return item.subItems.every((subItem: Any) =>
      validateNestingDepth(subItem, depth + 1, maxDepth),
    );
  }

  return true;
};
